/**
 * Loading vs Capacity — the whole surface, as one card with three levels inside it:
 * Group → entity → resource, and a week panel under the resource.
 *
 * It replaces the 13-column percentage heat map and its far-away cell drilldown. The
 * information change is the point: a week is no longer one load percentage, it is a stack of
 * capacity spent CLEARING backlog / work DUE to start this week / FREE capacity, with
 * STILL QUEUED hours above the line. That is what makes "scheduled because of its delivery
 * date" legible against "overspill from clearing backlog" — which the grid could not express
 * at all.
 *
 * Read-only. It does not schedule work. Its job is to answer "where is there room, and can
 * we take this on?" — including moving work between entities.
 *
 * NO SCHEDULED LOAD IS NOT AN EMPTY PAGE. This component used to return a whole-card
 * refusal whenever the window held no machine resource, which put it ABOVE the overview and
 * therefore above the trailing-utilisation card. Oracle has no backward-schedule spine
 * (G14: Red Ant's own load book is not in the warehouse yet) but it does have served
 * availability and utilisation, so scoping to Oracle hid the one capacity number that is
 * built for it. Overview.tsx now owns that state and suppresses only the parts that need a
 * load model.
 */

import React from 'react'
import BasisNotes from './BasisNotes'
import Overview from './Overview'
import ResourceView from './ResourceView'
import { LOAD_SITES, useSurfaceData, useSurfaceState } from '../../hooks/useLoadingSurface'
import type { Scope } from '../../hooks/useScope'
import type { Site } from '../../types/metrics'
import type { ResourceModel } from '../../viewmodels/loading'

const CARD = 'overflow-hidden rounded-xl border shadow-cx'

/** The open resource. Split out to hold the CD-1 function budget in Body; the week is
 *  clamped against the SERVED window rather than the chosen horizon, because a linked URL
 *  can carry a week the payload does not have and indexing off the end blanks the panel.
 *  Shortening the horizon does the same thing, which is the other reason this clamp is
 *  against what arrived rather than against what was asked for. */
function Resource({ r, levelled, state }: {
  r: ResourceModel
  levelled: boolean
  state: ReturnType<typeof useSurfaceState>
}): React.JSX.Element {
  return (
    <ResourceView
      r={r}
      levelled={levelled}
      week={Math.min(state.week, r.weeks.length - 1)}
      wo={state.wo}
      onWeek={state.setWeek}
      onWo={state.openWorksOrder}
      onBack={state.closeResource}
    />
  )
}

function Body({ data, scope, state }: {
  data: ReturnType<typeof useSurfaceData>
  scope: Scope
  state: ReturnType<typeof useSurfaceState>
}): React.JSX.Element {
  const open = data.resources.find((r) => r.id === state.resource) ?? null
  const levelled = state.view === 'levelled'
  if (data.isLoading && !data.resources.length) {
    return (
      <div role="status" className="animate-pulse px-[22px] py-12 text-center font-sans text-[13px]" style={{ color: 'var(--lc-mut)' }}>
        Loading schedule…
      </div>
    )
  }
  if (open) return <Resource r={open} levelled={levelled} state={state} />
  return (
    <Overview
      data={data}
      site={scope.site}
      levelled={levelled}
      from={scope.periodFrom}
      to={scope.periodTo}
      onOpen={state.openResource}
    />
  )
}

/** Entity and lens live in the page's ScopeBar (19 Aug 2026) — the header bar that carried
 *  them, and a breadcrumb duplicating the entity switch, is gone. ResourceView keeps its own
 *  "← Back to grid", so nothing lost a way back.
 *
 *  ⚠ ROUTING CONFIDENCE IS NOT ON THIS SURFACE (Mike, 1 Sep 2026). It graded the standards
 *  behind the load and never adjusted them, which is the right behaviour and the wrong
 *  audience: a client reading "4% high confidence" beside their own forward book takes it as
 *  a verdict on the book, not on our standards. It is an internal measure. The mart
 *  (`fct_routing_confidence`), the endpoint (`/api/v1/loading/confidence`) and the panel
 *  components all remain — nothing was deleted — they are simply not wired to a client
 *  screen. */
export default function LoadingSurface({ scope }: { scope: Scope }): React.JSX.Element {
  const state = useSurfaceState()
  const data = useSurfaceData(scope.site, state.horizon)
  const open = data.resources.find((r) => r.id === state.resource) ?? null
  const served = data.entities.map((e) => e.view).filter((v) => v !== undefined)
  return (
    <div className={CARD} style={{ background: 'var(--lc-card)', borderColor: 'var(--lc-b09)' }}>
      <Body data={data} scope={scope} state={state} />
      {served.length > 0 && <BasisNotes views={served} />}
    </div>
  )
}
